import clsx from 'clsx';
import React, {useRef, useState} from 'react';
import {CircularProgress, Stack} from '@mui/material';
import {
  Body,
  Icon,
  IconName,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import Sentry from '@/services/sentry/Sentry';
import {formatDueDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import type {ContentSubmissionState} from '@/types/contentSubmission';
import Checkbox from '@/components/checkbox';
import ContentSubmissionTag, {
  mapStateToTagVariant,
} from '@/components/content-submission-tag';
import CreatorNameLink from '@/components/creator-name-link';
import Dropdown, {DropdownOption} from '@/components/dropdown';
import styles from './SubComponents.module.scss';

// ─── CardFooterLabel ───────────────────────────────────────────────────────────

const FOOTER_LABEL_GAP = 0.5;

export type CardFooterLabelProps = {
  creatorName?: string;
  profileLink?: string;
  contentTypeLabel?: string;
  versionLabel?: string;
  uploadDateLabel?: string;
};

function CardFooterLabel({
  creatorName,
  profileLink,
  contentTypeLabel,
  versionLabel,
  uploadDateLabel,
}: CardFooterLabelProps) {
  if (!creatorName && !contentTypeLabel) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={FOOTER_LABEL_GAP}
    >
      {creatorName && (
        <CreatorNameLink name={creatorName} link={profileLink} size="md" />
      )}
      {contentTypeLabel && (
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate('asset-details', {
            context: uploadDateLabel ? undefined : 'noDate',
            contentType: `${contentTypeLabel}${versionLabel ?? ''}`,
            uploadDate: uploadDateLabel,
          })}
        </Body>
      )}
    </Stack>
  );
}

// ─── CardThumbnailArea ────────────────────────────────────────────────────────────

export type CardThumbnailAreaProps = {
  testID: string;
  thumbnailUrl?: string | null;
  videoUrl?: string | null;
  creatorName: string;
  aspectRatio?: string;
  contentTypeIcon?: IconName;
  latestVersionCommentCount: number;
  childContentSubmissionCount?: number;
  isStory?: boolean;
  isSelected: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  actionMenuOptions?: DropdownOption[];
  onActionChange: (option: DropdownOption) => void;
  onActionsMenuOpen?: () => void;
  onActionsMenuClose?: () => void;
};

function useVideoPreview() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isHoveringRef = useRef(false);
  const pendingPlayRef = useRef<Promise<void> | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handlePlayError = (error: unknown) => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      error.name === 'AbortError'
    ) {
      return;
    }

    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error))
    );
  };

  const resetVideo = (video: HTMLVideoElement) => {
    video.pause();
    video.currentTime = 0;
  };

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    if (!isReady) {
      setIsLoading(true);
    }
    const video = videoRef.current;
    if (!video || pendingPlayRef.current) {
      return;
    }

    const playPromise = video.play();
    pendingPlayRef.current = playPromise;
    playPromise.catch(handlePlayError).then(() => {
      if (pendingPlayRef.current !== playPromise) {
        return;
      }
      pendingPlayRef.current = null;
      if (!isHoveringRef.current) {
        resetVideo(video);
      }
    });
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    setIsLoading(false);
    const video = videoRef.current;
    if (video && !pendingPlayRef.current) {
      resetVideo(video);
    }
  };

  const handleCanPlay = () => {
    setIsReady(true);
    setIsLoading(false);
  };

  return {
    videoRef,
    isReady,
    isLoading,
    handleMouseEnter,
    handleMouseLeave,
    handleCanPlay,
  };
}

type VideoPreviewProps = {
  videoUrl: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  isReady: boolean;
  isLoading: boolean;
  onCanPlay: () => void;
};

function VideoPreview({
  videoUrl,
  videoRef,
  isReady,
  isLoading,
  onCanPlay,
}: VideoPreviewProps) {
  return (
    <>
      <video
        ref={videoRef}
        src={videoUrl}
        className={clsx(styles.videoPreview, isReady && styles.videoReady)}
        muted
        loop
        playsInline
        preload="none"
        onCanPlay={onCanPlay}
      />
      {isLoading ? (
        <div className={styles.videoLoaderOverlay}>
          <CircularProgress
            size={32}
            sx={{color: designSystemToken('semantic.bg.primary')}}
          />
        </div>
      ) : null}
    </>
  );
}

function CardThumbnailArea({
  testID,
  thumbnailUrl,
  videoUrl,
  creatorName,
  aspectRatio,
  contentTypeIcon,
  latestVersionCommentCount,
  isSelected,
  childContentSubmissionCount,
  isStory,
  onCheckboxChange,
  actionMenuOptions,
  onActionChange,
  onActionsMenuOpen,
  onActionsMenuClose,
}: CardThumbnailAreaProps) {
  const {
    videoRef,
    isReady,
    isLoading,
    handleMouseEnter,
    handleMouseLeave,
    handleCanPlay,
  } = useVideoPreview();

  return (
    <div
      className={styles.thumbnailWrapper}
      style={{aspectRatio}}
      onMouseEnter={videoUrl ? handleMouseEnter : undefined}
      onMouseLeave={videoUrl ? handleMouseLeave : undefined}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={creatorName}
          className={clsx(
            styles.thumbnail,
            isReady && styles.thumbnailWithVideo
          )}
        />
      ) : (
        <div className={styles.thumbnailPlaceholder} />
      )}
      {videoUrl ? (
        <VideoPreview
          videoUrl={videoUrl}
          videoRef={videoRef}
          isReady={isReady}
          isLoading={isLoading}
          onCanPlay={handleCanPlay}
        />
      ) : null}

      {onCheckboxChange ? (
        <div
          className={styles.checkboxOverlay}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onCheckboxChange(!isSelected);
          }}
        >
          <Checkbox
            className={styles.checkboxContainer}
            variant="light"
            checked={isSelected}
            onChange={() => {}}
          />
        </div>
      ) : null}

      {/* 3-dot menu — top right, visible on hover or when open */}
      {actionMenuOptions && actionMenuOptions.length > 0 && (
        <div
          className={styles.menuButtonWrapper}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Dropdown
            label=""
            options={actionMenuOptions}
            selectedIds={[]}
            onChange={onActionChange}
            menuPositionHorizontal="right"
            menuWidth={200}
            showItemDividers={false}
            shouldStopEventPropagation
            onOpen={onActionsMenuOpen}
            onClose={onActionsMenuClose}
            renderTrigger={({onClick, isOpen}) => (
              <button
                type="button"
                className={clsx(
                  styles.menuButton,
                  isOpen && styles.menuButtonVisible
                )}
                onClick={onClick}
                aria-label="More actions"
                data-testid={`${testID}-menu-button`}
              >
                <Icon name="Actions-More" size="medium" appearance="neutral" />
              </button>
            )}
          />
        </div>
      )}

      {/* Content type icon + story child count — bottom left */}
      <div className={styles.contentTypeOverlay}>
        {contentTypeIcon && (
          <Icon
            name={contentTypeIcon}
            size="medium"
            appearance="neutral"
            color={designSystemToken('semantic.fg.white')}
          />
        )}
        {isStory && childContentSubmissionCount ? (
          <Body size="md" color={designSystemToken('semantic.fg.white')}>
            {childContentSubmissionCount}
          </Body>
        ) : null}
      </div>

      {/* Comment count — bottom right */}
      {latestVersionCommentCount > 0 ? (
        <div className={styles.commentCountOverlay}>
          <Icon
            name="Actions-Comment2"
            size="small"
            appearance="neutral"
            color={designSystemToken('semantic.fg.neutral')}
          />
          <Body size="md" color={designSystemToken('semantic.fg.neutral')}>
            {latestVersionCommentCount}
          </Body>
        </div>
      ) : null}
    </div>
  );
}

// ─── CardInfoArea ─────────────────────────────────────────────────────────────

export type CardInfoAreaProps = {
  aasmState: ContentSubmissionState;
  pendingDepartments?: string[];
  approvedCount?: number;
  versionCount?: number;
  isOverdue?: boolean;
  dueAt?: string | null;
  aiScore: number | null;
  footerLabelProps: CardFooterLabelProps;
  showAiScore: boolean;
  postProductionCount?: number;
};

function CardInfoArea({
  aasmState,
  pendingDepartments,
  approvedCount = 0,
  versionCount,
  isOverdue,
  dueAt,
  aiScore,
  footerLabelProps,
  showAiScore,
  postProductionCount = 0,
}: CardInfoAreaProps) {
  return (
    <div className={styles.info}>
      <div className={styles.statusRow}>
        {isOverdue && (
          <Tooltip
            title={formatDueDate(dueAt)}
            placement="top"
            type="dark"
            arrow
          >
            <div>
              <ContentSubmissionTag variant="overdue" />
            </div>
          </Tooltip>
        )}
        <ContentSubmissionTag
          variant={mapStateToTagVariant(
            aasmState,
            pendingDepartments,
            approvedCount,
            versionCount
          )}
          departments={pendingDepartments}
        />
        {showAiScore ? (
          <ContentSubmissionTag variant="aiScore" score={aiScore} />
        ) : null}
        {postProductionCount > 0 ? (
          <ContentSubmissionTag
            variant="postProduction"
            count={postProductionCount}
          />
        ) : null}
      </div>
      <CardFooterLabel {...footerLabelProps} />
    </div>
  );
}

export {CardFooterLabel, CardThumbnailArea, CardInfoArea};
